import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import Chart from "react-apexcharts";
import { useDispatch } from "react-redux";

import ToastButton from "../../../Components/ExtraComponents/Alert_Toast";
import Loader from "../../../Utils/Loader";
import { GET_SUBADMIN_DASHBOARD_DATA } from "../../../Service/subadmin.service";

const SubadminDashboard = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Fetch from localStorage
  const userDetails = JSON.parse(localStorage.getItem("user_details"));
  const token = userDetails?.token;
  const subadmin_id = userDetails?.user_id;

  const gotodashboard = JSON.parse(localStorage.getItem("user_details_goTo"));
  const isgotodashboard = JSON.parse(localStorage.getItem("gotodashboard"));

  const [dashboardData, setDashboardData] = useState({
    loading: true,
    data: {},
    error: null,
  });

  const [activeView, setActiveView] = useState("cards");

  // Load view preference
  useEffect(() => {
    const saved = localStorage.getItem("activeView");
    if (saved) {
      setActiveView(saved);
      return;
    }

    const bodyClass = document.body.className;
    let view = "cards";

    if (bodyClass.includes("ocean-blue")) view = "cards";
    else if (bodyClass.includes("teal-corporate")) view = "cards";
    else if (bodyClass.includes("emerald-nature")) view = "cards";
    else if (bodyClass.includes("sunset-warm")) view = "cards";
    else if (bodyClass.includes("dark")) view = "cards";
    else if (bodyClass.includes("light")) view = "cards";
    else if (bodyClass.includes("rose-gold")) view = "cards";


    setActiveView(view);
    localStorage.setItem("activeView", view);
  }, []);

  // Fetch subadmin dashboard data
  const fetchDashboardData = useCallback(async () => {
    if (!token || !subadmin_id) {
      setDashboardData({
        loading: false,
        data: {},
        error: "Token or Subadmin ID missing.",
      });
      return;
    }

    try {


      setDashboardData((prev) => ({ ...prev, loading: true, error: null }));

      const response = await GET_SUBADMIN_DASHBOARD_DATA(token, isgotodashboard ? gotodashboard?.user_id : subadmin_id);

      if (response.status) {
        setDashboardData({
          loading: false,
          data: response.totalCount || {},
          error: null,
        });
      } else {
        setDashboardData({
          loading: false,
          data: {},
          error: "Failed to fetch subadmin dashboard data",
        });
      }
    } catch (error) {
      setDashboardData({
        loading: false,
        data: {},
        error: error.message || "Server error",
      });
    }
  }, [token, subadmin_id]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // Chart setup
  const getChartOptions = useMemo(
    () => (color) => ({
      chart: { type: "area", sparkline: { enabled: true } },
      stroke: { curve: "smooth", width: 2 },
      fill: {
        type: "gradient",
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.6,
          opacityTo: 0.05,
          stops: [0, 90, 100],
        },
      },
      tooltip: { enabled: false },
      colors: [color || "#00BFFF"],
    }),
    []
  );

  const chartSeries = useMemo(
    () => [{ name: "series-1", data: [10, 20, 15, 30, 25, 35, 28] }],
    []
  );

  // Cards for Subadmin Dashboard
  const cardConfig = useMemo(
    () => [
      {
        index: 1,
        name: "Total Clients",
        key: "total_client",
        route: "/subadmin/clients",
        visible: true,
        color: "#AB47BC",
      },
      {
        index: 2,
        name: "Active Clients",
        key: "active_client",
        route: "/subadmin/clients?filter=active",
        visible: true,
        color: "#66BB6A",
      },
      {
        index: 3,
        name: "Expired Clients",
        key: "total_expired_client",
        route: "/subadmin/clients?filter=inactive",  // ✅ correct route
        visible: true,
        color: "#dc3545",
      },


      // {
      //   index: 4,
      //   name: "Total Licenses",
      //   key: "all_licence", // ✅ backend key
      //   route: "/subadmin/licenses",
      //   visible: true,
      //   color: "#42A5F5",
      // },
      // {
      //   index: 5,
      //   name: "Used Licenses",
      //   key: "used_licence", // ✅ backend key
      //   route: "/subadmin/licenses?filter=used",
      //   visible: true,
      //   color: "#FF7043",
      // },
      // {
      //   index: 6,
      //   name: "Remaining Licenses",
      //   key: "remaining_licence", // ✅ backend key
      //   route: "/subadmin/licenses?filter=remaining",
      //   visible: true,
      //   color: "#26A69A",
      // },
    ],
    []
  );


  const sortedCards = useMemo(() => {
    return cardConfig.map((card) => ({
      ...card,
      value: dashboardData.data[card.key] ?? 0,
    }));
  }, [cardConfig, dashboardData.data]);

  if (dashboardData.error) {
    return (
      <div className="content-body">
        <div className="container-fluid">
          <div className="alert alert-danger" role="alert">
            <h4 className="alert-heading">Error Loading Dashboard</h4>
            <p>{dashboardData.error}</p>
            <button
              className="btn btn-outline-danger"
              onClick={fetchDashboardData}
            >
              Retry
            </button>
          </div>
        </div>
        <ToastButton />
      </div>
    );
  }

  return (
    <>
      <div className="content-body">
        <div className="container-fluid">
          <ol className="breadcrumb mb-5">
            <div className="col-lg-6">
              <li className="breadcrumb-item">
                <h4 className="font-w500 mb-2 fs-2">Subadmin Performance Board</h4>
                <h6 className="font-w500 mb-0">Your Overview</h6>
              </li>
            </div>
          </ol>

          {/* Compact View */}
          {activeView === "compactcards" && (
            <div className="compact-dashboard">
              {sortedCards.map((item) => (
                <div
                  key={item.index}
                  className="compact-item bg-white"
                  style={{ cursor: "pointer" }}
                  onClick={() => item.visible && navigate(item.route)}
                >
                  <div className="compact-content">
                    <div className="compact-icon">
                      <i className="fa fa-users" />
                    </div>
                    <div className="compact-info">
                      <h4 className="compact-title">{item.name}</h4>
                      <div className="compact-value">
                        {item.value.toLocaleString()}
                      </div>
                    </div>
                    <div className="d-flex flex-column">
                      {item.visible && (
                        <Link to={item.route} className="compact-link">
                          →
                        </Link>
                      )}
                      <div className="compact-chart">
                        <Chart
                          options={getChartOptions(item.color)}
                          series={chartSeries}
                          type="area"
                          height={40}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Card View */}

          {activeView === "cards" && (
            <div className="row">
              {sortedCards.map((item) => (
                <div
                  key={item.index}
                  className="col-xl-4 col-lg-3 col-sm-6 mb-4"
                >
                  {item.visible ? (
                    <Link
                      to={item.route}
                      className="text-decoration-none text-dark"
                    >
                      <div className="card card-dashboard h-100 border cursor-pointer">
                        <div className="card-body pb-2">
                          <div className="row justify-content-between align-items-center">
                            <div className="col-8 pe-0">
                              <h4 className="mb-1 text-truncate h5">
                                {item.name}
                              </h4>
                            </div>
                            <div className="col-4 text-end">
                              <h3 className="mb-0 fw-bold text-primary">
                                {item.value.toLocaleString()}
                              </h3>
                            </div>
                          </div>
                        </div>

                        <div className="col-12">
                          <Chart
                            options={getChartOptions(item.color)}
                            series={chartSeries}
                            type="area"
                            height={80}
                          />
                        </div>
                      </div>
                    </Link>
                  ) : (
                    <div className="card card-dashboard h-100 border">
                      <div className="card-body pb-2">
                        <h4 className="mb-1 text-truncate h5">{item.name}</h4>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

        </div>
      </div>

      <ToastButton />
    </>
  );
};

export default SubadminDashboard;
